import React from "react";
import { Grid, Typography } from "@mui/material";

import CtaButton from "@/clientCore/components/CtaButton";
import Label from "@/common/Label";
import OutlineTextInput from "@/common/OutlineTextInput";

import { useYumGiftCardBalanceCheck } from "./useYumGiftCardBalanceCheck";
import balanceCheckerStyles from "./styles";


type YumBalanceCheckerProps = {
    yumToken: string;
}

export const YumBalanceChecker = ({ yumToken }: YumBalanceCheckerProps) => {
    const classes = balanceCheckerStyles();
    const [
        {
            isLoading,
            balance,
            errorMessage,
            errors
        },
        {
            register,
            onCardNumberChanged,
            onPinNumberChanged,
            handleSubmit,
            handleBalanceCheck
        }
    ] = useYumGiftCardBalanceCheck({ yumToken });

    return (
        <Grid container className={classes.giftCardContent} data-testid="yum-gift-card-balance-checker">
            <form className={classes.giftCardFieldsContainer} onSubmit={handleSubmit?.((values) => handleBalanceCheck?.(values))}>
                <Grid item className={classes.giftCardInputContainer} >
                    <Label htmlFor="cardNumber">
                        <span>Gift card number</span>
                    </Label>
                    <OutlineTextInput
                        {...register?.("cardNumber", {
                            required: true,
                            minLength: 12,
                            maxLength: 16
                        })}
                        id="cardNumber"
                        name="cardNumber"
                        inputMode="numeric"
                        onChange={onCardNumberChanged}
                        maxLength={{ maxLength: 16 }}
                        error={!!errors?.cardNumber}
                    />
                    {errors?.cardNumber && (
                        <Typography className={classes.giftCardError}>
                            {errors.cardNumber.type === "required" 
                                ? 'Gift card number is required.' 
                                : errors.cardNumber.message || 'Please enter a valid gift card number.'
                            }
                        </Typography>
                    )}
                </Grid>
                <Grid item className={classes.giftCardInputContainer}>
                    <Label htmlFor="pin">
                        <span>PIN</span>
                    </Label>
                    <OutlineTextInput
                        {...register?.("pin", { required: true })}
                        id="pin"
                        name="pin"
                        onChange={onPinNumberChanged}
                        inputMode="numeric"
                        maxLength={{ maxLength: 4 }}
                        error={!!errors?.pin}
                    />
                    {errors?.pin
                        ? <Typography className={classes.giftCardError}>
                            {errors.pin.type === "required" ? 'PIN is required.' : 'Please enter valid PIN.'}
                        </Typography>
                        : null
                    }
                </Grid>
                {balance
                    ? <Grid item className={classes.giftCardInputContainer}>
                        <Label htmlFor="cardNumber">
                            <span>Card balance</span>
                        </Label>
                        <Typography>{balance}</Typography>
                    </Grid>
                    : null
                }
                {errorMessage
                    ? <Grid item className={classes.giftCardInputContainer}>
                        <Typography className={classes.giftCardError}>{errorMessage}</Typography>
                    </Grid>
                    : null
                }
                <Grid item className={classes.giftCardApplyButton}>
                    <CtaButton
                        color="secondary"
                        type="submit"
                        labelText={balance ? 'Check another card' : 'Check balance'}
                        loading={isLoading}
                        disabled={!!errors?.cardNumber || !!errors?.pin}
                    />
                </Grid>
            </form>
        </Grid>
    );
}
